import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowRight, MessageCircle, Activity, Box, Zap, Search, Shield, Menu, X, 
  Crosshair, TrendingUp, Radio, ArrowDownCircle, ChevronRight, 
  Brain, Globe, ArrowLeftRight, Sparkles, Key, ShieldCheck, ChevronDown,
  Bell, Users, Cookie
} from 'lucide-react';

// --- Assets ---
const ASSETS = {
  logo: "https://i.imgur.com/BZuYFYU.png",
  ico: "https://i.imgur.com/8kfjMgv.png",
  reel: [
    "https://i.imgur.com/4U64bsu.png",
    "https://i.imgur.com/FfBKVVT.png",
    "https://i.imgur.com/WItS2xq.png",
    "https://i.imgur.com/NBRr6Pe.png",
    "https://i.imgur.com/YrnvB9K.png",
    "https://i.imgur.com/DMuEPB3.png",
    "https://i.imgur.com/4dC8DzS.png",
    "https://i.imgur.com/uFIFol9.png"
  ]
};

// --- Components ---

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  // Define navigation items with IDs
  const navItems = [
    { name: 'Features', id: 'features' },
    { name: 'Suite', id: 'suite' },
    { name: 'Token', id: 'token' },
    { name: 'FAQ', id: 'faq' },
    { name: 'Community', id: 'community' },
  ];

  // Handle scroll to highlight active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150; // Offset for navbar height/padding

      for (const item of navItems) {
        const element = document.getElementById(item.id);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            setActiveSection(item.id);
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setIsOpen(false); // Close mobile menu
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // Navbar height
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <nav className="fixed top-0 w-full z-50 bg-[#090c1d]/80 backdrop-blur-md border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex-shrink-0 flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img src={ASSETS.logo} alt="AlphaRadarX" className="h-8 md:h-10 w-auto" />
          </div>
          
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-8">
              {navItems.map((item) => (
                <button 
                  key={item.name} 
                  onClick={() => scrollToSection(item.id)}
                  className={`${
                    activeSection === item.id 
                      ? 'text-[#36A4E6]' 
                      : 'text-gray-300 hover:text-white'
                  } px-3 py-2 rounded-md text-sm font-medium transition-colors`}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <button className="bg-gradient-to-r from-[#36A4E6] to-[#757EE0] text-white px-6 py-2 rounded-full font-medium hover:opacity-90 transition-all flex items-center gap-2 shadow-[0_0_25px_rgba(54,164,230,0.5)] hover:shadow-[0_0_40px_rgba(54,164,230,0.7)]">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse shadow-[0_0_10px_rgba(255,255,255,0.8)]"></div>
              Dashboard
            </button>
          </div>

          <div className="md:hidden flex items-center">
            <button onClick={() => setIsOpen(!isOpen)} className="text-gray-300 hover:text-white">
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden bg-[#090c1d] border-b border-white/10">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navItems.map((item) => (
              <button 
                key={item.name} 
                onClick={() => scrollToSection(item.id)}
                className={`${
                  activeSection === item.id 
                    ? 'text-[#36A4E6]' 
                    : 'text-gray-300 hover:text-white'
                } block px-3 py-2 rounded-md text-base font-medium w-full text-left`}
              >
                {item.name}
              </button>
            ))}
            <button className="w-full text-left bg-gradient-to-r from-[#36A4E6] to-[#757EE0] text-white px-3 py-2 rounded-md font-medium mt-4 flex items-center gap-2 shadow-[0_0_20px_rgba(54,164,230,0.4)]">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse shadow-[0_0_10px_rgba(255,255,255,0.8)]"></div>
              Dashboard
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

const TypewriterText = () => {
  const words = ["Advantage", "Intelligence", "Precision", "Predictive"];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentText, setCurrentText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(150);

  useEffect(() => {
    const handleTyping = () => {
      const fullText = words[currentWordIndex];
      
      if (isDeleting) {
        setCurrentText(fullText.substring(0, currentText.length - 1));
        setTypingSpeed(50);
      } else {
        setCurrentText(fullText.substring(0, currentText.length + 1));
        setTypingSpeed(150);
      }

      if (!isDeleting && currentText === fullText) {
        setTimeout(() => setIsDeleting(true), 2000); // Pause at end
      } else if (isDeleting && currentText === "") {
        setIsDeleting(false);
        setCurrentWordIndex((prev) => (prev + 1) % words.length);
      }
    };

    const timer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(timer);
  }, [currentText, isDeleting, currentWordIndex, typingSpeed]);

  return (
    <span className="bg-gradient-to-r from-[#36A4E6] to-[#757EE0] text-transparent bg-clip-text font-bold">
      {currentText}
      <span className="animate-pulse text-[#36A4E6]">|</span>
    </span>
  );
};

const Radar = () => {
  const [blips, setBlips] = useState([]);
  const [bgDots, setBgDots] = useState([]);
  const [statusText, setStatusText] = useState("SEARCHING...");
  const [scanAngle, setScanAngle] = useState(0);
  
  // Timer ref for the loop
  const loopTimeRef = useRef(0);

  // Initialize Background Dots
  useEffect(() => {
      const dots = Array.from({ length: 25 }).map((_, i) => ({
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          delay: Math.random() * 5,
          duration: 2 + Math.random() * 3
      }));
      setBgDots(dots);
  }, []);

  // Rotate the beam
  useEffect(() => {
      const interval = setInterval(() => {
          setScanAngle(prev => (prev + 2) % 360);
      }, 20);
      return () => clearInterval(interval);
  }, []);

  // Cycle status text
  useEffect(() => {
      const statuses = ["SEARCHING...", "ANALYZING", "DETECTED", "TRACKING"];
      let i = 0;
      const interval = setInterval(() => {
          i = (i + 1) % statuses.length;
          setStatusText(statuses[i]);
      }, 2000);
      return () => clearInterval(interval);
  }, []);

  // --- Content Generators ---
  const getRandomContent = () => {
    const tokens = ["SOL", "ETH", "BTC", "PEPE", "LINK", "ARB"];
    const actions = ["Accumulation", "Dump", "Bridge", "Swap", "Mint"];
    const randomToken = tokens[Math.floor(Math.random() * tokens.length)];
    const randomAmt = (Math.random() * 10).toFixed(1) + "M";
    
    const types = [
        { 
            label: "WHALE ALERT", 
            sub: `${actions[Math.floor(Math.random() * actions.length)]}: $${randomAmt} ${randomToken}`, 
            borderColor: "border-pink-500", 
            textColor: "text-pink-400",
            bgGradient: "from-pink-500/10 to-transparent",
            icon: <Activity size={16} /> 
        },
        { 
            label: "FRESH WALLET", 
            sub: `Sniper bot detected on ${randomToken}`, 
            borderColor: "border-purple-500", 
            textColor: "text-purple-400",
            bgGradient: "from-purple-500/10 to-transparent",
            icon: <Zap size={16} /> 
        },
        { 
            label: "SMART MONEY", 
            sub: `Top trader long ${randomToken} opened`, 
            borderColor: "border-green-500", 
            textColor: "text-green-400",
            bgGradient: "from-green-500/10 to-transparent",
            icon: <Search size={16} /> 
        },
        { 
            label: "EXCHANGE FLOW", 
            sub: `Stablecoin minting: $${(Math.random()*100).toFixed(0)}M`, 
            borderColor: "border-blue-500", 
            textColor: "text-blue-400",
            bgGradient: "from-blue-500/10 to-transparent",
            icon: <Box size={16} /> 
        }
    ];
    return types[Math.floor(Math.random() * types.length)];
  };

  // --- The 15s Loop Logic ---
  useEffect(() => {
    // Defined sequence of positions to loop through
    // x, y in percentages. Ensure they don't overlap.
    // 15 seconds loop = 15000ms
    const schedule = [
        { time: 500, x: 25, y: 30 },  // Top Left
        { time: 3500, x: 55, y: 65 }, // Bottom Rightish
        { time: 6500, x: 20, y: 70 }, // Bottom Left
        { time: 9500, x: 50, y: 25 }, // Top Rightish
        { time: 12500, x: 40, y: 50 } // Center
    ];

    const interval = setInterval(() => {
        // Current loop time in ms (0 to 15000)
        const currentTime = loopTimeRef.current;
        
        // Check if any event in schedule matches current time slice
        // We use a small window (100ms) to catch the event
        const event = schedule.find(e => Math.abs(e.time - currentTime) < 100);

        if (event) {
            const content = getRandomContent();
            const newBlip = {
                id: Date.now(),
                ...content,
                x: event.x,
                y: event.y
            };

            setBlips(prev => [...prev, newBlip]);

            // Auto-cleanup this specific blip from state after animation roughly ends (4s)
            // The visual removal is handled by CSS animation, this is just memory cleanup
            setTimeout(() => {
                setBlips(current => current.filter(b => b.id !== newBlip.id));
            }, 4000); 
        }

        // Increment loop time
        loopTimeRef.current += 100;
        if (loopTimeRef.current >= 15000) {
            loopTimeRef.current = 0; // Reset loop
        }

    }, 100);

    return () => clearInterval(interval);
  }, []);

  return (
    // Set max-h-[300px] on mobile to shrink the component
    <div className="relative w-full aspect-square max-w-[550px] mx-auto perspective-1000 group max-h-[300px] sm:max-h-full">
      {/* Grid Background */}
      <div 
        className="absolute inset-0 border border-white/10 rounded-2xl overflow-hidden bg-[#060813] shadow-[0_0_50px_rgba(0,0,0,0.5)]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(54, 164, 230, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(54, 164, 230, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}
      >
        
        {/* Flashing Background Dots */}
        {bgDots.map(dot => (
             <div 
                key={dot.id}
                className="absolute w-1 h-1 bg-[#36A4E6] rounded-full opacity-0"
                style={{ 
                    left: `${dot.x}%`, 
                    top: `${dot.y}%`,
                    animation: `twinkle ${dot.duration}s infinite ${dot.delay}s`
                }}
             ></div>
        ))}

        {/* Radar Concentric Circles */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[85%] h-[85%] border border-[#36A4E6]/10 rounded-full"></div>
          <div className="absolute w-[60%] h-[60%] border border-[#36A4E6]/10 rounded-full"></div>
          <div className="absolute w-[35%] h-[35%] border border-[#36A4E6]/10 rounded-full bg-[#36A4E6]/5"></div>
          {/* Crosshairs */}
          <div className="absolute w-full h-[1px] bg-[#36A4E6]/20"></div>
          <div className="absolute h-full w-[1px] bg-[#36A4E6]/20"></div>
          
          {/* Center Plus */}
          <Crosshair className="text-[#36A4E6]/50 absolute" size={24} />
        </div>

        {/* Scanning Beam Animation - Conic Gradient */}
        <div 
            className="absolute inset-0 rounded-full overflow-hidden"
            style={{ 
                maskImage: 'radial-gradient(circle, white 60%, transparent 100%)',
                WebkitMaskImage: 'radial-gradient(circle, white 60%, transparent 100%)'
            }}
        >
             <div 
                className="w-full h-full absolute inset-0"
                style={{
                    background: `conic-gradient(from ${scanAngle}deg, transparent 0deg, transparent 280deg, rgba(54, 164, 230, 0.1) 320deg, rgba(54, 164, 230, 0.6) 360deg)`,
                    transition: 'transform 0s linear'
                }}
             ></div>
        </div>

        {/* Live Feed Badge - INSIDE the frame now */}
        <div className="absolute top-4 right-4 z-30">
            <div className="inline-flex items-center gap-2 bg-[#0c1024]/80 border border-white/10 rounded-full px-3 py-1 backdrop-blur-md shadow-lg">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                <span className="text-[10px] font-medium text-gray-300 tracking-wide uppercase">Live Feed</span>
            </div>
        </div>


        {/* Blips / Cards */}
        {blips.map((blip) => (
          <div 
            key={blip.id}
            className="absolute z-20 pointer-events-none"
            style={{ left: `${blip.x}%`, top: `${blip.y}%` }}
          >
             {/* The Dot */}
             <div className="animate-lifecycle">
                 <div className="relative">
                     <div className={`absolute -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full ${blip.textColor.replace('text', 'bg')} shadow-[0_0_15px_currentColor] animate-ping`}></div>
                     <div className={`absolute -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full ${blip.textColor.replace('text', 'bg')}`}></div>

                     {/* The Card - Positioned relative to dot */}
                     <div className={`absolute left-4 top-[-20px] bg-[#0c1024]/95 border-l-2 ${blip.borderColor} py-2 px-3 rounded-r-md backdrop-blur-md min-w-[200px] shadow-lg flex flex-col gap-1`}>
                          {/* Glowing background gradient inside card */}
                          <div className={`absolute inset-0 bg-gradient-to-r ${blip.bgGradient} opacity-20 pointer-events-none`}></div>
                          
                          <div className={`flex items-center gap-2 text-xs font-bold ${blip.textColor} uppercase tracking-wider`}>
                            {blip.icon} {blip.label}
                          </div>
                          <div className="text-[10px] text-gray-300 font-mono border-t border-white/5 pt-1 mt-0.5 whitespace-nowrap">
                            {blip.sub}
                          </div>
                     </div>
                 </div>
             </div>
          </div>
        ))}

        {/* Status Overlay - Bottom Left */}
        <div className="absolute bottom-6 left-6 flex flex-col gap-1 z-30">
           <div className="text-[9px] text-gray-500 font-mono uppercase tracking-widest mb-1">System Status</div>
           <div className="flex items-center gap-2">
               <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-[0_0_10px_#22c55e]"></div>
               <span className="text-xs text-white font-mono font-bold tracking-wider">ONLINE // SCANNING</span>
           </div>
        </div>

        {/* Status Overlay - Bottom Right */}
        <div className="absolute bottom-6 right-6 text-right z-30">
           <span className="text-[9px] text-gray-500 font-mono uppercase tracking-widest mb-1 block">Target Acquisition</span>
           <span className="text-xs text-[#36A4E6] font-mono font-bold tracking-widest block uppercase animate-pulse">
             {statusText} <span className="animate-blink">|</span>
           </span>
        </div>
      </div>
    </div>
  );
};

// Component for the animated 2B+ number
const AnimatedNumber = ({ children }) => {
    return (
        <div className="relative inline-block">
            {/* Gradient Blur/Glow Background */}
            <div 
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full rounded-full opacity-20 blur-3xl animate-pulse-slow"
                style={{ background: 'radial-gradient(circle, #36A4E6 0%, #757EE0 50%, transparent 70%)' }}
            ></div>
            
            <span 
                className="font-extrabold text-[9rem] leading-none tracking-tight inline-block relative z-10 animate-gradient-shift"
                style={{
                    background: `linear-gradient(90deg, #36A4E6, #757EE0, #36A4E6, #757EE0)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundSize: '200% 100%',
                }}
            >
                {children}
            </span>
        </div>
    );
};

const LogoMarquee = () => {
  const reel = ASSETS.reel; 
  
  const LogoReelContent = () => (
    // This inner div contains the logos, forced to be the size of its content
    <div className="flex justify-around items-center px-16 gap-20 flex-shrink-0 min-w-max">
        {reel.map((url, i) => (
            <img 
                key={`l-${i}`} 
                src={url} 
                alt="Partner" 
                className="h-8 md:h-10 w-auto object-contain flex-shrink-0"
            />
        ))}
    </div>
  );

  return (
    <div className="w-full bg-[#090c1d] relative overflow-hidden"> 
        {/* Top Line */}
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-white/10"></div>
        
        {/* Container for content and spacing (py-16 for top/bottom padding) */}
        <div className="py-16">
            <div className="text-center mb-8">
                <span className="text-sm text-gray-500 uppercase tracking-widest">TRACKING LIQUIDITY ACROSS ALL MAJOR CHAINS</span>
            </div>
            
            {/* Logo Reel - The magic happens here: wrapper is 2x the content width */}
            <div className="flex w-[200%] animate-smooth-marquee">
                <LogoReelContent /> 
                <LogoReelContent />
            </div>
        </div>
        
        {/* Bottom Line */}
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-white/10"></div>
    </div>
  );
};

const StatsSection = () => (
    <div className="text-center pt-24 pb-8"> {/* Adjusted padding */}
        <div className="max-w-4xl mx-auto px-4 flex flex-col items-center">
            
            {/* 1. BADGE (Top row, Centered) */}
            <div className="inline-block bg-[#0f1429] text-[#36A4E6] text-xs font-semibold uppercase tracking-widest px-4 py-2 rounded-full border border-white/10 mb-8">
                SCALE & COVERAGE
            </div>
            
            {/* 2. 2B+ Animated Number (Next row) */}
            <AnimatedNumber>2B+</AnimatedNumber>


            {/* 3. Title (Next row) */}
            <h2 className="text-2xl font-bold tracking-wider uppercase mb-6 mt-8 text-white/90">
                Transactions Analyzed
            </h2>

            {/* 4. Paragraph Text (Next row) */}
            <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
                AlphaRadarX continuously scans the entire onchain ecosystem, monitoring Smart Money behavior, whale movements, and liquidity flows across every major chain.
            </p>

            {/* NEW: Updated for Mobile responsiveness */}
            <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-6 mt-6 text-sm text-gray-500 font-mono tracking-widest uppercase">
                <span>Real-time indexing</span>
                <span className="hidden sm:inline-block">•</span>
                <span>Sub-second alerts</span>
                <span className="hidden sm:inline-block">•</span>
                <span>Cross-chain visibility</span>
            </div>
        </div>
    </div>
);

// --- Feature Tabs Component ---
const FeatureTabs = () => {
    const [activeTab, setActiveTab] = useState(0);

    const features = [
        {
            id: 'smart-money',
            title: "Smart Money Tracking",
            desc: "See what the top 0.01 percent of wallets are doing in real time.",
            icon: <TrendingUp size={20} />,
            colorClass: "text-[#22c55e]",
            bgClass: "bg-[#22c55e]/20",
            borderClass: "border-[#22c55e]",
            link: "https://alpharadarx.gitbook.io/alpharadarx-whitepaper/core-features/smart-money-tracking", // Added Link
            content: {
                eyebrow: "Radar Insight",
                title: "Smart Money Tracking",
                description: "AlphaRadarX identifies and labels high-performance wallets based on PnL, trading patterns, and behavioral clustering.\n\nSee when top traders open positions, rotate capital, or exit early, all in real time."
            }
        },
        {
            id: 'whale-movements',
            title: "Whale Movements",
            desc: "Trace multi-million-dollar flows across all major chains.",
            icon: <Radio size={20} />,
            colorClass: "text-[#ef4444]", // RED
            bgClass: "bg-[#ef4444]/20",
            borderClass: "border-[#ef4444]",
            link: "https://alpharadarx.gitbook.io/alpharadarx-whitepaper/core-features/whale-movements", // Added Link
            content: {
                eyebrow: "Intelligence",
                title: "Whale Movement Intelligence",
                description: "Follow whale-level transaction flows across Ethereum, Solana, BSC, Base, Polygon, Avalanche, Sui, and Arbitrum.\n\nMonitor large accumulations, mergers, capital rotations, and sudden multi-chain moves."
            }
        },
        {
            id: 'exchange-inflows',
            title: "Exchange Inflows",
            desc: "Detect high-risk liquidity spikes into CEXs instantly.",
            icon: <ArrowDownCircle size={20} />,
            colorClass: "text-[#eab308]", 
            bgClass: "bg-[#eab308]/20",
            borderClass: "border-[#eab308]",
            link: "https://alpharadarx.gitbook.io/alpharadarx-whitepaper/core-features/exchange-inflow-monitor", // Added Link
            content: {
                eyebrow: "Feature Insight",
                title: "Exchange Inflow Detection",
                description: "AlphaRadarX monitors liquidity movement into major exchanges to flag potential sell pressure and market risk.\n\nWhen 10,000+ ETH moves into Binance, you’ll know instantly."
            }
        },
        {
            id: 'emerging-trends',
            title: "Emerging Trends",
            desc: "Spot fresh wallets accumulating early before the chart moves.",
            icon: <Zap size={20} />,
            colorClass: "text-[#a855f7]", // Purple (CHANGED)
            bgClass: "bg-[#a855f7]/20", 
            borderClass: "border-[#a855f7]",
            link: "https://alpharadarx.gitbook.io/alpharadarx-whitepaper/core-features/emerging-trend-detection", // Added Link
            content: {
                eyebrow: "Precision View",
                title: "Emerging Trend Discovery",
                description: "Fresh wallets often enter positions before a token becomes visible to the broader market.\n\nAlphaRadarX highlights early accumulation waves so traders can capture new trends at their origin."
            }
        }
    ];

    const getActiveHex = () => {
        switch(activeTab) {
            case 0: return "#22c55e"; // Green
            case 1: return "#ef4444"; // Red
            case 2: return "#eab308"; // Yellow
            case 3: return "#a855f7"; // Purple (NEW)
            default: return "#36A4E6"; // Blue (default)
        }
    };
    const activeHex = getActiveHex();

    // --- Deterministic Animations for Emerging Trends ---
    // Fixed array of dust particles (instead of random)
    const dustParticles = [
        { cx: 20, cy: 30, durX: '15s', durY: '18s', delay: '0s' },
        { cx: 280, cy: 350, durX: '20s', durY: '22s', delay: '1s' },
        { cx: 150, cy: 150, durX: '25s', durY: '20s', delay: '2s' },
        { cx: 40, cy: 280, durX: '18s', durY: '16s', delay: '3s' },
        { cx: 200, cy: 50, durX: '22s', durY: '25s', delay: '0.5s' },
        { cx: 100, cy: 320, durX: '19s', durY: '21s', delay: '1.5s' },
        { cx: 250, cy: 120, durX: '24s', durY: '23s', delay: '2.5s' },
        { cx: 80, cy: 200, durX: '17s', durY: '19s', delay: '4s' }
    ];

    // Fixed array of popup locations (Safe zones, avoiding Top-Right Badge)
    // Badge is roughly Top-Right (x > 200, y < 100)
    const trendPopups = [
        { id: 1, x: 40, y: 60, addr: '0x7a2...b4', delay: '0s', dur: '4s' },       // Top Left
        { id: 2, x: 50, y: 340, addr: '0x3c4...e1', delay: '2s', dur: '5s' },      // Bottom Left
        { id: 3, x: 240, y: 300, addr: '0x1f9...22', delay: '0.5s', dur: '4.5s' }, // Bottom Right
        { id: 4, x: 260, y: 180, addr: '0x9b1...c3', delay: '1.5s', dur: '4s' },   // Mid Right (Below badge)
        { id: 5, x: 130, y: 160, addr: '0x4d2...f9', delay: '3s', dur: '5.5s' },   // Center
        { id: 6, x: 160, y: 350, addr: '0xa1b...88', delay: '1s', dur: '4.8s' },   // Bottom Center
        { id: 7, x: 30, y: 200, addr: '0x8e3...11', delay: '2.5s', dur: '5.2s' },  // Mid Left
        { id: 8, x: 120, y: 80, addr: '0x2c9...aa', delay: '3.5s', dur: '4.2s' }   // Top Center (Safe from badge)
    ];

    return (
        <div id="features" className="pt-16 pb-24 bg-[#090c1d] relative"> {/* Adjusted top padding to pt-16 */}
            <div className="max-w-7xl mx-auto px-4">
                <div className="text-center mb-16">
                      <h2 className="text-3xl md:text-5xl font-bold">
                        Revealing the next generation of <br/> 
                        <span className="text-[#36A4E6]">onchain market intelligence</span>
                    </h2>
                </div>

                <div className="grid lg:grid-cols-12 gap-8 items-start">
                    
                    {/* Left Column: Navigation Tabs */}
                    <div className="lg:col-span-4 flex flex-col gap-4">
                        {features.map((feature, index) => (
                            <button
                                key={feature.id}
                                onClick={() => setActiveTab(index)}
                                className={`text-left p-6 rounded-xl border transition-all duration-300 group ${
                                    activeTab === index 
                                        ? `bg-[#151a30] ${feature.borderClass}/50 shadow-[0_0_20px_rgba(34,197,94,0.15)]` // Using generic shadow for now
                                        : 'bg-transparent border-transparent hover:bg-white/5'
                                }`}
                            >
                                <div className="flex items-start gap-4">
                                    <div className={`mt-1 p-2 rounded-lg transition-colors ${
                                        activeTab === index ? `${feature.bgClass} ${feature.colorClass}` : 'bg-white/5 text-gray-400 group-hover:text-white'
                                    }`}>
                                            {feature.icon}
                                    </div>
                                    <div>
                                        <h3 className={`font-bold text-lg mb-1 ${activeTab === index ? 'text-white' : 'text-gray-300 group-hover:text-white'}`}>
                                            {feature.title}
                                        </h3>
                                        <p className="text-sm text-gray-500 leading-relaxed">
                                            {feature.desc}
                                        </p>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Right Column: Detailed Content Panel */}
                    <div className="lg:col-span-8">
                        <div className="relative bg-[#0f1429] border border-white/10 rounded-2xl p-8 md:p-12 overflow-hidden min-h-[600px] flex flex-col md:flex-row gap-8 items-center">
                             
                             {/* Content Side */}
                             <div className="flex-1 relative z-10 space-y-6">
                                <div className={`${features[activeTab].colorClass} text-xs font-bold tracking-widest uppercase mb-2`}>
                                    {features[activeTab].content.eyebrow}
                                </div>
                                <h3 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                                    {features[activeTab].content.title}
                                </h3>
                                <div className="text-gray-400 leading-relaxed space-y-4 text-base md:text-lg">
                                    {features[activeTab].content.description.split('\n\n').map((paragraph, idx) => (
                                        <p key={idx}>{paragraph}</p>
                                    ))}
                                </div>
                                {/* Updated Button to Anchor Tag with dynamic link */}
                                <a 
                                    href={features[activeTab].link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`inline-flex items-center gap-2 text-white font-semibold hover:${features[activeTab].colorClass} transition-colors mt-4 group`}
                                >
                                    Learn more <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
                                </a>
                             </div>

                             {/* Visual Side - Custom Tech Graph */}
                             <div className="flex-1 w-full max-w-sm relative self-stretch flex items-center justify-center">
                                {/* Vertical Box Container */}
                                <div className="relative w-full aspect-[4/5] bg-[#0a0d1d] rounded-xl border border-white/10 p-6 shadow-2xl overflow-hidden">
                                    
                                    {/* Moving Blur Background */}
                                    <div className={`absolute top-1/2 left-1/2 w-32 h-32 blur-[60px] rounded-full animate-drift-slow -translate-x-1/2 -translate-y-1/2 pointer-events-none z-0`}
                                         style={{ backgroundColor: `${activeHex}33` }} // 33 is 20% opacity in hex
                                    ></div>

                                    {/* Grid Overlay */}
                                    <div className="absolute inset-0 z-0" style={{
                                        backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
                                        backgroundSize: '40px 40px'
                                    }}></div>
                                    
                                    {/* Chart Label Pill - DYNAMIC COLOR */}
                                    <div className={`absolute top-6 right-6 bg-[#0f1d2e] border rounded-full px-3 py-1 flex items-center gap-2 z-20`}
                                          style={{ borderColor: `${activeHex}4d` }} // 4d is 30%
                                    >
                                        <div className={`w-1.5 h-1.5 rounded-full animate-pulse`} style={{ backgroundColor: activeHex }}></div>
                                        <span className={`text-[10px] font-bold tracking-wider uppercase`} style={{ color: activeHex }}>
                                            {features[activeTab].title.toUpperCase()}
                                        </span>
                                    </div>

                                    {/* SVG Chart Visualization */}
                                    <svg className="w-full h-full relative z-10" viewBox="0 0 300 400">
                                        
                                        {/* Axis - Conditionally Render for Non-Whale (1), Non-Exchange (2), Non-Trends (3) Tabs */}
                                        {activeTab !== 1 && activeTab !== 2 && activeTab !== 3 && (
                                            <>
                                                {/* Y-Axis Details */}
                                                <g stroke="rgba(255,255,255,0.1)" strokeWidth="1">
                                                    <line x1="40" y1="40" x2="40" y2="360" /> {/* Y Axis Line */}
                                                    {/* Y Ticks */}
                                                    <line x1="35" y1="100" x2="45" y2="100" />
                                                    <line x1="35" y1="180" x2="45" y2="180" />
                                                    <line x1="35" y1="260" x2="45" y2="260" />
                                                </g>
                                                <g fill="rgba(255,255,255,0.3)" fontSize="8" fontFamily="monospace" textAnchor="end">
                                                    <text x="32" y="103">1.6k</text>
                                                    <text x="32" y="183">1.2k</text>
                                                    <text x="32" y="263">0.8k</text>
                                                </g>

                                                {/* X-Axis Details */}
                                                <g stroke="rgba(255,255,255,0.1)" strokeWidth="1">
                                                    <line x1="40" y1="360" x2="280" y2="360" /> {/* X Axis Line */}
                                                    {/* X Ticks */}
                                                    <line x1="100" y1="355" x2="100" y2="365" />
                                                    <line x1="180" y1="355" x2="180" y2="365" />
                                                    <line x1="260" y1="355" x2="260" y2="365" />
                                                </g>
                                                <g fill="rgba(255,255,255,0.3)" fontSize="8" fontFamily="monospace" textAnchor="middle">
                                                    <text x="100" y="375">4h</text>
                                                    <text x="180" y="375">8h</text>
                                                    <text x="260" y="375">12h</text>
                                                </g>
                                            </>
                                        )}
                                        
                                        {/* --- ANIMATIONS BASED ON TAB --- */}
                                        
                                        {activeTab === 0 && (
                                            <>
                                                {/* Smart Money Curve */}
                                                <path
                                                    d="M 40 340 C 90 340, 120 280, 160 200 S 240 60, 280 50"
                                                    fill="none"
                                                    stroke={activeHex}
                                                    strokeWidth="3"
                                                    strokeLinecap="round"
                                                    className="drop-shadow-[0_0_10px_currentColor] animate-draw-line"
                                                    style={{ color: activeHex }}
                                                    strokeDasharray="500"
                                                    strokeDashoffset="500"
                                                />

                                                {/* Smart Entry Point */}
                                                <g className="animate-appear-early opacity-0">
                                                    <circle cx="115" cy="285" r="4" fill="#090c1d" stroke={activeHex} strokeWidth="2" />
                                                    <rect x="85" y="250" width="70" height="24" rx="4" fill="#1e293b" stroke={activeHex} strokeWidth="1" opacity="0.9" />
                                                    <text x="120" y="265" textAnchor="middle" fill={activeHex} fontSize="10" fontFamily="monospace" fontWeight="bold">Smart Entry</text>
                                                    <line x1="115" y1="274" x2="115" y2="281" stroke={activeHex} strokeWidth="1" />
                                                </g>

                                                {/* Accumulation Point */}
                                                <g className="animate-appear-mid opacity-0">
                                                    <circle cx="190" cy="145" r="4" fill="#fff" />
                                                    <rect x="150" y="110" width="80" height="24" rx="4" fill="#0c1024" stroke="rgba(255,255,255,0.2)" />
                                                    <text x="190" y="125" textAnchor="middle" fill={activeHex} fontSize="10" fontFamily="monospace" fontWeight="bold">Accumulation</text>
                                                    <line x1="190" y1="134" x2="190" y2="141" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeDasharray="2 2" />
                                                </g>
                                            </>
                                        )}

                                        {activeTab === 1 && (
                                            <>
                                                {/* Whale Path Definition (Used for Motion and Drawing) */}
                                                <defs>
                                                    <path id="whalePath" d="M -20 200 C 80 80, 120 320, 200 200 S 320 80, 350 200" />
                                                </defs>

                                                {/* Faint Background Path */}
                                                <path d="M -20 200 C 80 80, 120 320, 200 200 S 320 80, 350 200" stroke="rgba(255,255,255,0.1)" strokeWidth="1" fill="none" />

                                                {/* The Drawing Line (Trail) */}
                                                <path
                                                    d="M -20 200 C 80 80, 120 320, 200 200 S 320 80, 350 200"
                                                    fill="none"
                                                    stroke={activeHex}
                                                    strokeWidth="3"
                                                    strokeLinecap="round"
                                                    strokeDasharray="600"
                                                    strokeDashoffset="600"
                                                    className="drop-shadow-[0_0_10px_currentColor]"
                                                    style={{ color: activeHex }}
                                                >
                                                    <animate attributeName="stroke-dashoffset" from="600" to="-600" dur="6s" repeatCount="indefinite" />
                                                </path>
                                                
                                                {/* The Comet Head (Dot moving along path) */}
                                                <circle r="4" fill="#fff">
                                                    <animateMotion dur="6s" repeatCount="indefinite">
                                                        <mpath href="#whalePath" />
                                                    </animateMotion>
                                                </circle>

                                                {/* 1. First Discovery - 1,000 BTC */}
                                                <g>
                                                    {/* Expanding Ripple - Approx at 30% of 6s = 1.8s */}
                                                    <circle cx="95" cy="180" r="0" stroke={activeHex} strokeWidth="3" fill="none" opacity="0">
                                                        {/* Fixed: Use SMIL animation for maximum compatibility */}
                                                        <animate attributeName="r" values="0;0;40;40" keyTimes="0;0.3;0.45;1" dur="6s" repeatCount="indefinite" />
                                                        <animate attributeName="opacity" values="0;0.8;0;0" keyTimes="0;0.3;0.45;1" dur="6s" repeatCount="indefinite" />
                                                        <animate attributeName="stroke-width" values="0;3;0;0" keyTimes="0;0.3;0.45;1" dur="6s" repeatCount="indefinite" />
                                                    </circle>
                                                    
                                                    {/* Label Reveal */}
                                                    <g className="animate-reveal-1 opacity-0">
                                                        <rect x="65" y="145" width="70" height="24" rx="4" fill="#1e293b" stroke={activeHex} strokeWidth="1" opacity="0.9" />
                                                        <text x="100" y="160" textAnchor="middle" fill={activeHex} fontSize="10" fontFamily="monospace" fontWeight="bold">1,000 BTC</text>
                                                        <line x1="95" y1="169" x2="95" y2="176" stroke={activeHex} strokeWidth="1" />
                                                    </g>
                                                </g>

                                                {/* 2. Second Discovery - High Vol Tx */}
                                                <g>
                                                     {/* Expanding Ripple - Approx at 70% of 6s = 4.2s */}
                                                     <circle cx="250" cy="160" r="0" stroke={activeHex} strokeWidth="3" fill="none" opacity="0">
                                                        {/* Fixed: Use SMIL animation for maximum compatibility */}
                                                        <animate attributeName="r" values="0;0;40;40" keyTimes="0;0.7;0.85;1" dur="6s" repeatCount="indefinite" />
                                                        <animate attributeName="opacity" values="0;0.8;0;0" keyTimes="0;0.7;0.85;1" dur="6s" repeatCount="indefinite" />
                                                        <animate attributeName="stroke-width" values="0;3;0;0" keyTimes="0;0.7;0.85;1" dur="6s" repeatCount="indefinite" />
                                                     </circle>

                                                     {/* Label Reveal */}
                                                     <g className="animate-reveal-2 opacity-0">
                                                        <rect x="215" y="180" width="80" height="24" rx="4" fill="#0c1024" stroke="rgba(255,255,255,0.2)" />
                                                        <text x="255" y="195" textAnchor="middle" fill={activeHex} fontSize="10" fontFamily="monospace" fontWeight="bold">High Vol Tx</text>
                                                        <line x1="250" y1="164" x2="250" y2="176" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeDasharray="2 2" />
                                                     </g>
                                                </g>
                                            </>
                                        )}

                                        {/* --- EXCHANGE INFLOWS (Tab 2 - Yellow) --- */}
                                        {activeTab === 2 && (
                                            <>
                                                {/* Central Hub */}
                                                <circle cx="150" cy="200" r="15" fill="#1e293b" stroke={activeHex} strokeWidth="2" className="animate-pulse" />
                                                <circle cx="150" cy="200" r="6" fill={activeHex} />

                                                {/* Incoming Dots Animation Definition */}
                                                <defs>
                                                    {/* Paths from edges to center */}
                                                    <path id="pathTopLeft" d="M 20 20 L 150 200" />
                                                    <path id="pathTopRight" d="M 280 40 L 150 200" />
                                                    <path id="pathBottom" d="M 150 380 L 150 200" />
                                                    <path id="pathLeft" d="M 10 200 L 150 200" />
                                                </defs>

                                                {/* Inflow Particles Group 1 - White */}
                                                <circle r="3" fill="white">
                                                    <animateMotion dur="2s" repeatCount="indefinite" begin="0s">
                                                        <mpath href="#pathTopLeft" />
                                                    </animateMotion>
                                                    <animate attributeName="opacity" values="0;1;1;0" dur="2s" repeatCount="indefinite" />
                                                </circle>
                                                <circle r="3" fill="white">
                                                    <animateMotion dur="2.5s" repeatCount="indefinite" begin="0.5s">
                                                        <mpath href="#pathTopRight" />
                                                    </animateMotion>
                                                    <animate attributeName="opacity" values="0;1;1;0" dur="2.5s" repeatCount="indefinite" />
                                                </circle>
                                                 <circle r="3" fill="white">
                                                    <animateMotion dur="3s" repeatCount="indefinite" begin="1s">
                                                        <mpath href="#pathBottom" />
                                                    </animateMotion>
                                                    <animate attributeName="opacity" values="0;1;1;0" dur="3s" repeatCount="indefinite" />
                                                </circle>

                                                {/* Inflow Particles Group 2 - Yellow */}
                                                <circle r="4" fill={activeHex}>
                                                    <animateMotion dur="1.8s" repeatCount="indefinite" begin="0.2s">
                                                        <mpath href="#pathLeft" />
                                                    </animateMotion>
                                                    <animate attributeName="opacity" values="0;1;1;0" dur="1.8s" repeatCount="indefinite" />
                                                </circle>
                                                <circle r="4" fill={activeHex}>
                                                    <animateMotion dur="2.2s" repeatCount="indefinite" begin="1.2s">
                                                        <mpath href="#pathTopRight" />
                                                    </animateMotion>
                                                    <animate attributeName="opacity" values="0;1;1;0" dur="2.2s" repeatCount="indefinite" />
                                                </circle>

                                                {/* Alert 1: Sell Pressure */}
                                                <g className="animate-reveal-1 opacity-0">
                                                    <rect x="170" y="160" width="100" height="24" rx="4" fill="#1e293b" stroke={activeHex} strokeWidth="1" opacity="0.9" />
                                                    <text x="220" y="175" textAnchor="middle" fill={activeHex} fontSize="10" fontFamily="monospace" fontWeight="bold">SELL PRESSURE</text>
                                                    <line x1="165" y1="185" x2="170" y2="180" stroke={activeHex} strokeWidth="1" />
                                                </g>

                                                {/* Alert 2: Risk Warning */}
                                                <g className="animate-reveal-2 opacity-0">
                                                    <rect x="30" y="220" width="100" height="24" rx="4" fill="#0c1024" stroke={activeHex} strokeWidth="1" opacity="0.9" />
                                                    <text x="80" y="235" textAnchor="middle" fill={activeHex} fontSize="10" fontFamily="monospace" fontWeight="bold">RISK OF SELLING</text>
                                                    <line x1="130" y1="225" x2="135" y2="215" stroke={activeHex} strokeWidth="1" />
                                                </g>
                                            </>
                                        )}

                                        {/* --- EMERGING TRENDS (Tab 3 - Purple) --- */}
                                        {activeTab === 3 && (
                                            <>
                                                {/* Background: Floating White Dust/Stars (Deterministic) */}
                                                {dustParticles.map((d, i) => (
                                                    <circle key={`dust-${i}`} r={1.5} fill="white" opacity="0.3">
                                                        <animate 
                                                            attributeName="cx" 
                                                            values={`${d.cx};${(d.cx + 50) % 300};${d.cx}`} 
                                                            dur={d.durX} 
                                                            repeatCount="indefinite" 
                                                        />
                                                        <animate 
                                                            attributeName="cy" 
                                                            values={`${d.cy};${(d.cy + 50) % 400};${d.cy}`} 
                                                            dur={d.durY} 
                                                            repeatCount="indefinite" 
                                                        />
                                                        <animate attributeName="opacity" values="0;0.5;0" dur="4s" begin={d.delay} repeatCount="indefinite" />
                                                    </circle>
                                                ))}

                                                {/* Floating Purple Orbs - Kept subtle */}
                                                <circle cx="50" cy="100" r="40" fill={activeHex} opacity="0.1">
                                                     <animate attributeName="r" values="30;50;30" dur="6s" repeatCount="indefinite" />
                                                     <animate attributeName="opacity" values="0.1;0.2;0.1" dur="6s" repeatCount="indefinite" />
                                                </circle>
                                                <circle cx="250" cy="300" r="30" fill={activeHex} opacity="0.1">
                                                     <animate attributeName="r" values="20;40;20" dur="5s" repeatCount="indefinite" />
                                                     <animate attributeName="opacity" values="0.1;0.15;0.1" dur="5s" repeatCount="indefinite" />
                                                </circle>

                                                {/* Deterministic Popups (Fixed Nesting Issue) */}
                                                {trendPopups.map((popup) => (
                                                    // OUTER GROUP handles POSITIONING (Static Transform)
                                                    <g 
                                                        key={popup.id} 
                                                        transform={`translate(${popup.x}, ${popup.y})`}
                                                    >
                                                        {/* INNER GROUP handles ANIMATION (CSS Transform) */}
                                                        <g 
                                                            style={{ 
                                                                animation: `pop-fade-scale ${popup.dur} ease-in-out infinite ${popup.delay}` 
                                                            }}
                                                            className="opacity-0" // Start hidden, animation controls opacity
                                                        >
                                                            <circle r="6" fill={activeHex} opacity="0.8" className="animate-ping" />
                                                            <circle r="4" fill="white" />
                                                            
                                                            {/* Tooltip Smart Positioning */}
                                                            <g transform={`translate(${popup.x > 150 ? -90 : 10}, -10)`}>
                                                                <rect width="80" height="20" rx="4" fill="#0c1024" stroke={activeHex} strokeWidth="1" opacity="0.9" />
                                                                <text x="40" y="14" textAnchor="middle" fill="white" fontSize="10" fontFamily="monospace">
                                                                    {popup.addr}
                                                                </text>
                                                            </g>
                                                        </g>
                                                    </g>
                                                ))}
                                            </>
                                        )}

                                    </svg>

                                    {/* Decor Elements */}
                                    <div className="absolute bottom-4 left-4 right-4 h-8 bg-gradient-to-t from-[#0a0d1d] to-transparent pointer-events-none"></div>
                                </div>
                             </div>

                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

// --- NEW PLATFORM SUITE SECTION (Chainlink Style) ---
const PlatformSuite = () => {
  const tools = [
    {
      title: "Smart Money Engine",
      desc: "Behavioral analysis system that identifies elite wallets using PnL, history, win-rate, clustering, and fund-level patterns.",
      icon: <Brain size={28} className="animate-pulse" />, // Animation Added
      color: "text-[#22c55e]",
      bg: "bg-[#22c55e]/10",
      borderHover: "group-hover:border-[#22c55e]/50"
    },
    {
      title: "Whale Flow Scanner",
      desc: "Multi-chain tracker that visualizes large capital movements across Ethereum, Solana, BSC, Base, Polygon, Arbitrum, Avalanche, Sui, and more.",
      icon: <Globe size={28} className="animate-spin-slow" />, // Animation Added
      color: "text-[#ef4444]",
      bg: "bg-[#ef4444]/10",
      borderHover: "group-hover:border-[#ef4444]/50"
    },
    {
      title: "Exchange Flow Monitor",
      desc: "Real-time detection of liquidity entering or leaving major exchanges, revealing sell pressure, accumulation, or structural risk.",
      icon: <ArrowLeftRight size={28} className="animate-pulse" />, // Animation Added
      color: "text-[#eab308]",
      bg: "bg-[#eab308]/10",
      borderHover: "group-hover:border-[#eab308]/50"
    },
    {
      title: "Trend Discovery AI",
      desc: "Early-stage pattern detection that highlights fresh wallets entering positions before tokens show up on charts or social feeds.",
      icon: <Sparkles size={28} className="animate-pulse" />, // Animation Added
      color: "text-[#a855f7]",
      bg: "bg-[#a855f7]/10",
      borderHover: "group-hover:border-[#a855f7]/50"
    }
  ];

  return (
    <div id="suite" className="py-24 bg-[#090c1d] relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-[#36A4E6]/5 blur-[100px] rounded-full pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="text-center mb-16 max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
            The <span className="text-[#36A4E6]">comprehensive intelligence suite</span> for crypto traders.
          </h2>
          <p className="text-lg text-gray-400">
            Access all the tools you need to understand, track, and anticipate onchain activity, in one unified platform.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tools.map((tool, idx) => (
            <div 
              key={idx}
              className={`group bg-[#0f1429] border border-white/10 rounded-xl p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${tool.borderHover}`}
            >
              <div className={`w-14 h-14 rounded-lg ${tool.bg} flex items-center justify-center mb-6 transition-transform group-hover:scale-110`}>
                <div className={tool.color}>
                  {tool.icon}
                </div>
              </div>
              
              <h3 className="text-xl font-bold text-white mb-3 group-hover:text-white transition-colors">
                {tool.title}
              </h3>
              
              <p className="text-gray-400 text-sm leading-relaxed">
                {tool.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Learn More Button */}
        <div className="mt-12 text-center">
            <a 
                href="https://alpharadarx.gitbook.io/alpharadarx-whitepaper" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#36A4E6] to-[#757EE0] text-white px-8 py-3 rounded-full font-bold hover:opacity-90 transition-all shadow-[0_0_20px_rgba(54,164,230,0.3)] hover:shadow-[0_0_30px_rgba(54,164,230,0.5)] group"
            >
                Learn More <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </a>
        </div>

      </div>
    </div>
  );
};

// --- Utility Section Star Data (Deterministic for SSR/performance) ---
const utilityStars = Array.from({ length: 60 }).map((_, i) => ({
    id: i,
    top: `${Math.random() * 100}%`,
    left: `${Math.random() * 100}%`,
    // Randomize star size between two small options
    size: Math.random() > 0.7 ? 'w-1 h-1' : 'w-0.5 h-0.5',
    // Randomize initial opacity and animation timings
    baseOpacity: Math.random() * 0.3 + 0.1, // 0.1 to 0.4
    animationDelay: `${Math.random() * 5}s`,
    animationDuration: `${Math.random() * 4 + 3}s` // 3s to 7s
}));

// --- NEW UTILITY SECTION: "Orbiting Utilities" ---
const UtilitySection = () => {
  return (
    <div id="token" className="py-24 bg-[#050711] relative overflow-hidden border-t border-white/5">
      
      {/* --- NEW: Universe Star Background --- */}
      <div className="absolute inset-0 z-0 pointer-events-none">
          {utilityStars.map(star => (
              <div
                  key={star.id}
                  className={`absolute rounded-full bg-white ${star.size} animate-star-twinkle`}
                  style={{
                      top: star.top,
                      left: star.left,
                      '--base-opacity': star.baseOpacity, // Pass CSS variable for base opacity
                      animationDelay: star.animationDelay,
                      animationDuration: star.animationDuration
                  }}
              ></div>
          ))}
      </div>

      {/* Content (Added relative and z-10 to sit above stars) */}
      <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-16 items-center relative z-10">
        
        {/* Left: Text Content */}
        <div className="space-y-8">
          <div>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              The Utility Layer <br/>
              <span className="text-[#757EE0]">Behind AlphaRadarX</span>
            </h2>
            <p className="text-xl text-gray-300 font-medium">
              $ARX is the access key that upgrades your visibility, speed, and control.
            </p>
          </div>

          <p className="text-gray-400">
            AlphaRadarX is free to explore, but $ARX unlocks the full intelligence stack.
          </p>

          <ul className="space-y-6">
            <li className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#36A4E6]/10 text-[#36A4E6] flex items-center justify-center">
                <Key size={24} />
              </div>
              <div>
                <h4 className="text-white font-bold mb-1">Access Tiers</h4>
                <p className="text-gray-400 text-sm">Hold $ARX to remove delays, unlock pro dashboards, and enable advanced Smart Money filters.</p>
              </div>
            </li>
            <li className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#eab308]/10 text-[#eab308] flex items-center justify-center">
                <Zap size={24} />
              </div>
              <div>
                <h4 className="text-white font-bold mb-1">Signal Boosts</h4>
                <p className="text-gray-400 text-sm">Use $ARX to prioritize your Signal Alerts, get earlier notifications, and higher frequency updates.</p>
              </div>
            </li>
            <li className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#ef4444]/10 text-[#ef4444] flex items-center justify-center">
                <Crosshair size={24} />
              </div>
              <div>
                <h4 className="text-white font-bold mb-1">Deep Wallet Intel</h4>
                <p className="text-gray-400 text-sm">Spend $ARX to run advanced Address Intelligence scans: clustering, risk scoring, and historical PnL traces.</p>
              </div>
            </li>
            <li className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#22c55e]/10 text-[#22c55e] flex items-center justify-center">
                <ShieldCheck size={24} />
              </div>
              <div>
                <h4 className="text-white font-bold mb-1">Governance</h4>
                <p className="text-gray-400 text-sm">Stake $ARX to vote on new chains, data sources, and product features.</p>
              </div>
            </li>
          </ul>

          <div className="pt-4 border-t border-white/10">
            <p className="text-sm text-gray-500 font-mono">
              $ARX turns AlphaRadarX from a viewer into a full trading advantage.
            </p>
          </div>
        </div>

        {/* Right: Orbit Animation */}
        <div className="relative flex justify-center items-center">
          
          {/* SVG Orbit Visualization Container */}
          <div className="relative w-full max-w-[500px] aspect-square">
            
            {/* Background Gradient Blob (Moved INSIDE to respect square aspect ratio) */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#36A4E6]/10 to-[#757EE0]/10 blur-[80px] rounded-full transform scale-90"></div>

            <svg className="w-full h-full relative z-10" viewBox="0 0 400 400">
              
              {/* Central Glowing Coin Definitions */}
              <defs>
                <radialGradient id="coinGradient" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#36A4E6" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="#757EE0" stopOpacity="0.2" />
                </radialGradient>
                <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="10" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {/* Orbit Path Ring */}
              <circle cx="200" cy="200" r="140" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
              <circle cx="200" cy="200" r="140" fill="none" stroke="url(#coinGradient)" strokeWidth="2" strokeDasharray="20 860" strokeLinecap="round" className="animate-spin-slow" style={{ opacity: 0.5 }} />

              {/* Central Token */}
              <g className="animate-pulse-svg">
                <circle cx="200" cy="200" r="40" fill="#0f1429" stroke="#36A4E6" strokeWidth="2" filter="url(#glow)" />
                {/* Fixed Text Centering */}
                <text x="200" y="200" dominantBaseline="central" textAnchor="middle" fill="#fff" fontSize="16" fontWeight="bold" fontFamily="sans-serif">ARX</text>
              </g>

              {/* Orbiting Group */}
              <g>
                <animateTransform 
                  attributeName="transform" 
                  type="rotate" 
                  from="0 200 200" 
                  to="360 200 200" 
                  dur="20s" 
                  repeatCount="indefinite" 
                />

                {/* Icon 1: Key (Access) - Top */}
                <g transform="translate(200, 60)">
                  {/* Counter-rotate to keep icon upright */}
                  <animateTransform attributeName="transform" type="rotate" from="360" to="0" dur="20s" repeatCount="indefinite" additive="sum" />
                  
                  <circle r="24" fill="#0f1429" stroke="#56b4e6" strokeWidth="2" className="group-hover:fill-[#56b4e6]" />
                  <Key size={20} x="-10" y="-10" stroke="#56b4e6" />
                </g>

                {/* Icon 2: Zap (Signals) - Right */}
                <g transform="translate(340, 200)">
                  <animateTransform attributeName="transform" type="rotate" from="360" to="0" dur="20s" repeatCount="indefinite" additive="sum" />
                  
                  <circle r="24" fill="#0f1429" stroke="#eab308" strokeWidth="2" />
                  <Zap size={20} x="-10" y="-10" stroke="#eab308" />
                </g>

                {/* Icon 3: Crosshair (Intel) - Bottom */}
                <g transform="translate(200, 340)">
                  <animateTransform attributeName="transform" type="rotate" from="360" to="0" dur="20s" repeatCount="indefinite" additive="sum" />
                  
                  <circle r="24" fill="#0f1429" stroke="#ef4444" strokeWidth="2" />
                  <Crosshair size={20} x="-10" y="-10" stroke="#ef4444" />
                </g>

                {/* Icon 4: Shield (Governance) - Left */}
                <g transform="translate(60, 200)">
                  <animateTransform attributeName="transform" type="rotate" from="360" to="0" dur="20s" repeatCount="indefinite" additive="sum" />
                  
                  <circle r="24" fill="#0f1429" stroke="#22c55e" strokeWidth="2" />
                  <ShieldCheck size={20} x="-10" y="-10" stroke="#22c55e" />
                </g>
              </g>

            </svg>
          </div>
        </div>

      </div>
    </div>
  );
};

// --- NEW FAQ SECTION ---
const FAQSection = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const faqs = [
    {
      q: "What makes AlphaRadarX different from traditional blockchain explorers",
      a: "Traditional explorers are designed to display raw transactional data. They do not interpret behavior, identify patterns, or classify wallets based on performance. AlphaRadarX transforms this raw information into structured intelligence by analyzing clusters, detecting Smart Money activity, evaluating liquidity movement, and highlighting early trend formation. This shift from data display to behavioral interpretation allows traders to see the market the way professional funds and algorithmic systems view it."
    },
    {
      q: "How does AlphaRadarX determine which wallets qualify as Smart Money",
      a: "Smart Money designation is based on a consistent record of high performance across multiple market cycles. The system evaluates timing precision, conviction in accumulation, risk adjusted returns, liquidity preferences, exit consistency, and behavioral stability. Wallets that repeatedly demonstrate strong decision making under different market conditions are classified as Smart Money. This process is fully data driven and updated continuously as new information becomes available."
    },
    {
      q: "Does AlphaRadarX require users to connect their personal wallet",
      a: "No. The platform does not ask users to connect wallets, share private information, or expose their trading activity. All intelligence is presented without requiring personal onchain access. This ensures privacy, reduces risk, and maintains complete anonymity for all users while still enabling deep market insights."
    },
    {
      q: "How fast are the alerts delivered",
      a: "Alerts are generated and delivered in near real time. The system is designed to detect and process events within a fraction of a second, then transmit them immediately through the user interface or mobile notifications. This speed is essential for capturing Smart Money entries, large exchange inflows, rapid accumulation waves, and other market moving signals."
    },
    {
      q: "Which blockchains are supported",
      a: "AlphaRadarX supports major ecosystems including Ethereum, Solana, BNB Chain, Base, Arbitrum, Polygon, Avalanche, Sui, and additional networks determined through governance and internal research. Each chain undergoes full indexing and is integrated into the unified intelligence pipeline to ensure consistent interpretation across ecosystems."
    },
    {
      q: "How does the platform detect early trend formation",
      a: "Trend detection emerges from studying behavioral patterns rather than price alone. The system identifies early signals such as synchronized cluster accumulation, inflow alignment across chains, increased participation from fresh wallets, rising Smart Money conviction, and liquidity expansion in specific sectors. These indicators often appear well before market charts reflect a developing trend."
    },
    {
      q: "Is AlphaRadarX suitable for both retail traders and institutions",
      a: "Yes. Retail users benefit from clear intelligence summaries, real time alerts, wallet analysis, and trend discovery tools. Institutions gain access to deeper capabilities, including the Institutional API, custom intelligence models, multi entity monitoring, research grade datasets, and enterprise level support. The platform scales with the sophistication of the user."
    }
  ];

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div id="faq" className="py-24 bg-[#050711] border-t border-white/5">
      <div className="max-w-4xl mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">Frequently Asked Questions</h2>
        
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className={`border-b border-white/10 overflow-hidden transition-colors duration-300 ${activeIndex === index ? 'bg-white/5 rounded-lg border-none' : ''}`}
            >
              <button 
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left focus:outline-none group"
              >
                <div className="flex items-center gap-4">
                  {/* Glowing Dot */}
                  <div className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    activeIndex === index 
                      ? 'bg-[#36A4E6] shadow-[0_0_10px_#36A4E6]' 
                      : 'bg-gray-600 group-hover:bg-gray-400'
                  }`}></div>
                  
                  <span className={`text-lg font-medium transition-colors ${activeIndex === index ? 'text-white' : 'text-gray-300 group-hover:text-white'}`}>
                    {faq.q}
                  </span>
                </div>
                
                <ChevronDown 
                  className={`text-gray-500 transition-transform duration-300 ${activeIndex === index ? 'rotate-180 text-[#36A4E6]' : ''}`} 
                  size={20} 
                />
              </button>
              
              <div 
                className={`grid transition-all duration-300 ease-in-out ${
                  activeIndex === index ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                }`}
              >
                <div className="overflow-hidden">
                  <div className="p-6 pt-0 text-gray-400 leading-relaxed">
                    {faq.a}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// --- NEW JOIN COMMUNITY SECTION ---
const CommunitySection = () => {
  return (
    <div id="community" className="py-24 relative overflow-hidden bg-[#090c1d] border-t border-white/5">
        
        {/* --- Background Animation Layer --- */}
        <div className="absolute inset-0 z-0 pointer-events-none">
            
            {/* 1. Moving Grid Floor (FASTER) */}
            <div 
                className="absolute inset-0 opacity-30" // Increased opacity
                style={{
                    perspective: '1000px',
                }}
            >
                <div 
                    className="absolute inset-0 w-full h-[200%] origin-top"
                    style={{
                        backgroundImage: `
                            linear-gradient(rgba(54, 164, 230, 0.4) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(54, 164, 230, 0.4) 1px, transparent 1px)
                        `,
                        backgroundSize: '50px 50px',
                        transform: 'rotateX(60deg)',
                        animation: 'grid-scroll 10s linear infinite', // FASTER animation
                    }}
                ></div>
            </div>

            {/* 2. Pulsing Aurora Bottom (FASTER & MORE VIVID) */}
            <div className="absolute bottom-0 left-0 right-0 h-[500px] bg-gradient-to-t from-[#36A4E6]/20 via-[#757EE0]/10 to-transparent animate-pulse-fast"></div>
            
            {/* 3. Floating Particles (MORE & FASTER) */}
            {[...Array(30)].map((_, i) => ( // More particles
                <div
                    key={i}
                    className="absolute bg-[#36A4E6] rounded-full opacity-40 animate-float-particle-fast" // Higher opacity, faster animation
                    style={{
                        width: Math.random() * 3 + 1 + 'px', // Smaller
                        height: Math.random() * 3 + 1 + 'px', // Smaller
                        left: Math.random() * 100 + '%',
                        top: Math.random() * 100 + '%',
                        animationDuration: Math.random() * 5 + 5 + 's', // Faster duration
                        animationDelay: Math.random() * 3 + 's',
                    }}
                ></div>
            ))}
        </div>

        {/* --- CSS for this section's specific animations --- */}
        <style>{`
            @keyframes grid-scroll {
                0% { transform: rotateX(60deg) translateY(0); }
                100% { transform: rotateX(60deg) translateY(-50px); }
            }
            @keyframes float-particle-fast {
                0% { transform: translateY(0) translateX(0) rotate(0deg); opacity: 0; }
                20% { opacity: 0.6; }
                80% { opacity: 0.6; }
                100% { transform: translateY(-200px) translateX(${Math.random() > 0.5 ? 50 : -50}px) rotate(360deg); opacity: 0; }
            }
            @keyframes pulse-fast {
                0%, 100% { opacity: 0.5; transform: scale(1); }
                50% { opacity: 0.8; transform: scale(1.05); }
            }
            .animate-pulse-fast {
                animation: pulse-fast 3s infinite ease-in-out;
            }
            .animate-float-particle-fast {
                animation: float-particle-fast infinite linear;
            }
        `}</style>

        <div className="max-w-7xl mx-auto px-4 relative z-10">
            {/* Header */}
            <div className="text-center mb-16">
                <h2 className="text-3xl md:text-5xl font-bold mb-6">
                    Join the <span className="text-[#36A4E6]">AlphaRadarX</span> Ecosystem
                </h2>
                <p className="text-gray-400 max-w-2xl mx-auto text-lg">
                    Don't trade alone. Connect with elite traders, get instant updates, and shape the future of onchain intelligence.
                </p>
            </div>

            {/* Cards Grid */}
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {/* X Card */}
                <a href="https://x.com/alpharaderx" target="_blank" rel="noreferrer" className="group relative p-1 rounded-2xl bg-gradient-to-b from-white/10 to-white/5 hover:from-[#36A4E6]/50 hover:to-[#757EE0]/50 transition-all duration-500 hover:-translate-y-2">
                    <div className="absolute inset-0 bg-[#0f1429] rounded-2xl m-[1px]"></div>
                    <div className="relative bg-[#0f1429] rounded-xl p-8 h-full flex flex-col items-center text-center hover:bg-[#0f1429]/80 transition-colors justify-center min-h-[180px]"> {/* Reduced min-h */}
                        {/* X Icon: Brand Styled */}
                        <div className="w-12 h-12 rounded-xl bg-[#36A4E6]/10 border border-[#36A4E6]/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-all duration-300 group-hover:bg-[#36A4E6]/20 group-hover:border-[#36A4E6]/50 group-hover:shadow-[0_0_20px_rgba(54,164,230,0.4)]">
                            <svg className="w-6 h-6 text-[#36A4E6]" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-bold text-white group-hover:text-[#36A4E6] transition-colors">Follow on X</h3>
                    </div>
                </a>

                {/* Telegram Community */}
                <a href="https://t.me/AlphaRadar_X" target="_blank" rel="noreferrer" className="group relative p-1 rounded-2xl bg-gradient-to-b from-white/10 to-white/5 hover:from-[#36A4E6]/50 hover:to-[#757EE0]/50 transition-all duration-500 hover:-translate-y-2">
                    <div className="absolute inset-0 bg-[#0f1429] rounded-2xl m-[1px]"></div>
                    <div className="relative bg-[#0f1429] rounded-xl p-8 h-full flex flex-col items-center text-center hover:bg-[#0f1429]/80 transition-colors justify-center min-h-[180px]"> {/* Reduced min-h */}
                        {/* Telegram Icon: Brand Styled */}
                        <div className="w-12 h-12 rounded-xl bg-[#36A4E6]/10 border border-[#36A4E6]/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-all duration-300 group-hover:bg-[#36A4E6]/20 group-hover:border-[#36A4E6]/50 group-hover:shadow-[0_0_20px_rgba(54,164,230,0.4)]">
                            <svg className="w-6 h-6 text-[#36A4E6]" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20.665 3.717l-17.73 6.837c-1.21.486-1.203 1.161-.222 1.462l4.552 1.42 10.532-6.645c.498-.303.953-.14.579.192l-8.533 7.701h-.002l.002.001-.314 4.692c.46 0 .663-.211.921-.46l2.211-2.15 4.599 3.397c.848.467 1.457.227 1.668-.785l3.019-14.228c.309-1.239-.473-1.8-1.282-1.434z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-bold text-white mb-0 group-hover:text-[#36A4E6] transition-colors">Join Discussion</h3>
                    </div>
                </a>

                {/* Telegram News */}
                <a href="https://t.me/AlphaRadar_Xnews" target="_blank" rel="noreferrer" className="group relative p-1 rounded-2xl bg-gradient-to-b from-white/10 to-white/5 hover:from-[#36A4E6]/50 hover:to-[#757EE0]/50 transition-all duration-500 hover:-translate-y-2">
                    <div className="absolute inset-0 bg-[#0f1429] rounded-2xl m-[1px]"></div>
                    <div className="relative bg-[#0f1429] rounded-xl p-8 h-full flex flex-col items-center text-center hover:bg-[#0f1429]/80 transition-colors justify-center min-h-[180px]"> {/* Reduced min-h */}
                        {/* Telegram Icon: Brand Styled */}
                        <div className="w-12 h-12 rounded-xl bg-[#36A4E6]/10 border border-[#36A4E6]/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-all duration-300 group-hover:bg-[#36A4E6]/20 group-hover:border-[#36A4E6]/50 group-hover:shadow-[0_0_20px_rgba(54,164,230,0.4)]">
                            <svg className="w-6 h-6 text-[#36A4E6]" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20.665 3.717l-17.73 6.837c-1.21.486-1.203 1.161-.222 1.462l4.552 1.42 10.532-6.645c.498-.303.953-.14.579.192l-8.533 7.701h-.002l.002.001-.314 4.692c.46 0 .663-.211.921-.46l2.211-2.15 4.599 3.397c.848.467 1.457.227 1.668-.785l3.019-14.228c.309-1.239-.473-1.8-1.282-1.434z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-bold text-white mb-0 group-hover:text-[#36A4E6] transition-colors">Official News</h3>
                    </div>
                </a>
            </div>
        </div>
    </div>
  )
}

// --- NEW COOKIE BANNER SECTION ---
const CookieBanner = ({ onAccept, onPreferences }) => {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] bg-[#090c1d]/95 backdrop-blur-xl border-t border-white/10 p-4 shadow-[0_-10px_50px_rgba(54,164,230,0.15)] transition-all duration-500">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-center gap-6">
        
        {/* Left: Icon and Text */}
        <div className="flex items-center gap-4 text-center md:text-left flex-1 justify-center md:justify-start">
          {/* Glowing Cookie Icon */}
          <div className="relative flex-shrink-0 hidden sm:block">
            <div className="absolute inset-0 bg-[#36A4E6] blur-[15px] opacity-60 rounded-full animate-pulse"></div>
            <div className="relative w-10 h-10 rounded-full bg-[#0f1429] border border-[#36A4E6]/30 flex items-center justify-center shadow-inner">
              <Cookie className="text-[#36A4E6]" size={20} />
            </div>
          </div>
          <p className="text-sm text-gray-300">
            We use cookies to enhance your experience and analyze platform traffic. <span className="hidden sm:inline">By continuing, you consent to our use of cookies.</span>
          </p>
        </div>
        
        {/* Right: Actions */}
        <div className="flex items-center gap-4 flex-shrink-0">
          <button 
            onClick={onPreferences}
            className="text-sm text-gray-400 hover:text-white transition-colors underline underline-offset-4 decoration-white/20 hover:decoration-white/50"
          >
            Preferences
          </button>
          <button 
            onClick={onAccept} 
            className="bg-gradient-to-r from-[#36A4E6] to-[#757EE0] text-white px-6 py-2.5 rounded-full font-bold text-sm hover:opacity-90 transition-all shadow-[0_0_20px_rgba(54,164,230,0.3)] hover:shadow-[0_0_30px_rgba(54,164,230,0.5)] active:scale-95"
          >
            Accept All
          </button>
        </div>

      </div>
    </div>
  );
};

// --- NEW COOKIE PREFERENCES MODAL ---
const CookiePreferencesModal = ({ onClose, onSave }) => {
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div 
        className="bg-[#0f1429] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-modal-in"
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex justify-between items-center relative">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#36A4E6] to-[#757EE0]"></div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Cookie className="text-[#36A4E6]" size={20} />
            Cookie Preferences
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>
        
        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          <p className="text-gray-400 text-sm">
            Manage your cookie settings below. You can update your preferences at any time from the footer link.
          </p>

          <div className="space-y-6 pt-4 border-t border-white/5">
            {/* Essential */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-bold text-white mb-1">Strictly Necessary</h3>
                <p className="text-xs text-gray-400">Required for the website to function securely and properly. These cannot be disabled.</p>
              </div>
              <div className="w-11 h-6 bg-[#36A4E6]/50 rounded-full relative opacity-50 cursor-not-allowed flex-shrink-0">
                <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full"></div>
              </div>
            </div>
            
            {/* Analytics */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-bold text-white mb-1">Analytics & Performance</h3>
                <p className="text-xs text-gray-400">Helps us understand how visitors interact with our platform so we can improve it.</p>
              </div>
              <button 
                onClick={() => setAnalytics(!analytics)}
                className={`w-11 h-6 rounded-full relative transition-colors duration-300 flex-shrink-0 ${analytics ? 'bg-[#36A4E6]' : 'bg-gray-600'}`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform duration-300 ${analytics ? 'left-6' : 'left-1'}`}></div>
              </button>
            </div>

            {/* Marketing */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-bold text-white mb-1">Marketing & Targeting</h3>
                <p className="text-xs text-gray-400">Used to deliver relevant advertisements and track their performance across platforms.</p>
              </div>
              <button 
                onClick={() => setMarketing(!marketing)}
                className={`w-11 h-6 rounded-full relative transition-colors duration-300 flex-shrink-0 ${marketing ? 'bg-[#36A4E6]' : 'bg-gray-600'}`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform duration-300 ${marketing ? 'left-6' : 'left-1'}`}></div>
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 flex justify-end gap-4 bg-[#0a0d1d]">
          <button 
            onClick={onClose}
            className="px-6 py-2.5 rounded-full font-medium text-white hover:bg-white/5 transition-colors border border-white/10 text-sm"
          >
            Cancel
          </button>
          <button 
            onClick={() => onSave({ analytics, marketing })}
            className="bg-gradient-to-r from-[#36A4E6] to-[#757EE0] text-white px-6 py-2.5 rounded-full font-bold hover:opacity-90 transition-all shadow-[0_0_15px_rgba(54,164,230,0.3)] hover:shadow-[0_0_25px_rgba(54,164,230,0.5)] text-sm active:scale-95"
          >
            Save My Preferences
          </button>
        </div>
      </div>
    </div>
  );
}

const App = () => {
  const [showCookies, setShowCookies] = useState(true);
  const [showPreferences, setShowPreferences] = useState(false);

  // --- SEO & Favicon Setup ---
  useEffect(() => {
    // Page Title
    document.title = "AlphaRadarX | Onchain Market Intelligence";

    // Helper to dynamically set meta tags
    const setMeta = (name, content, attribute = "name") => {
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Standard SEO
    setMeta('description', 'AlphaRadarX reveals Smart Money movements, whale flows, and cross-chain signals in real time, giving traders true X-Ray vision into the crypto markets.');
    setMeta('keywords', 'AlphaRadarX, onchain data, smart money tracking, whale alerts, crypto trading, blockchain intelligence, DeFi screener');
    setMeta('author', 'AlphaRadarX');
    
    // Open Graph / Facebook / LinkedIn
    setMeta('og:title', 'AlphaRadarX | Onchain Market Intelligence', 'property');
    setMeta('og:description', 'AlphaRadarX reveals Smart Money movements, whale flows, and cross-chain signals in real time.', 'property');
    setMeta('og:image', ASSETS.logo, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('og:url', window.location.href, 'property');
    
    // Twitter
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', 'AlphaRadarX | Onchain Market Intelligence');
    setMeta('twitter:description', 'AlphaRadarX reveals Smart Money movements, whale flows, and cross-chain signals in real time.');
    setMeta('twitter:image', ASSETS.logo);

    // Favicon Setup
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = ASSETS.ico;
  }, []);

  const handleAcceptAll = () => {
    setShowCookies(false);
  };

  const handleSavePreferences = (prefs) => {
    setShowPreferences(false);
    setShowCookies(false);
  };

  return (
    <div className="min-h-screen bg-[#090c1d] text-white overflow-x-hidden font-sans selection:bg-[#36A4E6] selection:text-[#090c1d]">
      <style>{`
        /* UPDATED Marquee Animation DEFINITION: Critical fix for seamless loop */
        @keyframes smooth-marquee {
          0% { transform: translateX(0); }
          /* Must translate exactly 50% to move the two 50% sections */
          100% { transform: translateX(-50%); } 
        }
        .animate-smooth-marquee {
          animation: smooth-marquee 30s linear infinite;
        }

        /* Gradient Shift Animation for 2B+ */
        @keyframes gradient-shift {
            0% { background-position: 0% 50%; }
            100% { background-position: 100% 50%; }
        }
        .animate-gradient-shift {
            animation: gradient-shift 4s linear infinite;
        }
        
        /* Slow Pulse for Glow Effect (HTML Elements - includes translate) */
        @keyframes pulse-slow {
            0%, 100% { opacity: 0.2; transform: translate(-50%, -50%) scale(1); }
            50% { opacity: 0.4; transform: translate(-50%, -50%) scale(1.05); }
        }
        .animate-pulse-slow {
            animation: pulse-slow 5s infinite ease-in-out;
        }

        /* NEW: Pulse for SVG Elements (Scale ONLY, NO translate) */
        @keyframes pulse-svg-only {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.9; transform: scale(1.05); }
        }
        .animate-pulse-svg {
            animation: pulse-svg-only 3s infinite ease-in-out;
            transform-origin: 200px 200px;
            transform-box: fill-box;
        }

        /* NEW: Subtle Twinkle for Utility Section Stars */
        @keyframes star-twinkle {
            0%, 100% { opacity: var(--base-opacity, 0.3); transform: scale(1); }
            50% { opacity: 1; transform: scale(1.2); }
        }
        .animate-star-twinkle {
            animation: star-twinkle infinite ease-in-out;
        }

        /* Slow Spin for Globe Icon */
        @keyframes spin-slow {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
            animation: spin-slow 12s linear infinite;
        }


        /* Existing Animations */
        @keyframes lifecycle {
            0% { opacity: 0; transform: translateY(10px) scale(0.9); }
            10% { opacity: 1; transform: translateY(0) scale(1); }
            85% { opacity: 1; transform: translateY(0) scale(1); }
            100% { opacity: 0; transform: translateY(-10px) scale(0.95); }
        }
        .animate-lifecycle {
            animation: lifecycle 3.5s ease-out forwards;
        }

        @keyframes twinkle {
            0%, 100% { opacity: 0; }
            50% { opacity: 0.5; }
        }

        /* --- New Chart Animations (Fixed Loop) --- */
        
        /* 1. Line Drawing Loop */
        @keyframes draw-line-loop {
            0% { stroke-dashoffset: 500; opacity: 0; }
            10% { opacity: 1; }
            50% { stroke-dashoffset: 0; opacity: 1; } /* Fully drawn by middle */
            80% { stroke-dashoffset: 0; opacity: 1; } /* Hold */
            90% { opacity: 0; } /* Fade out */
            100% { stroke-dashoffset: 500; opacity: 0; } /* Reset */
        }
        .animate-draw-line {
            animation: draw-line-loop 8s ease-in-out infinite;
        }

        /* 2. Early Label Appearance */
        @keyframes appear-early-loop {
            0%, 15% { opacity: 0; transform: translateY(5px); }
            20%, 80% { opacity: 1; transform: translateY(0); }
            90%, 100% { opacity: 0; transform: translateY(-5px); }
        }
        .animate-appear-early {
            animation: appear-early-loop 8s ease-out infinite;
        }

        /* 3. Mid Label Appearance (UPDATED: Starts earlier at 30%) */
        @keyframes appear-mid-loop {
            0%, 30% { opacity: 0; transform: translateY(5px); }
            35%, 80% { opacity: 1; transform: translateY(0); }
            90%, 100% { opacity: 0; transform: translateY(-5px); }
        }
        .animate-appear-mid {
            animation: appear-mid-loop 8s ease-out infinite;
        }

        /* --- WHALE ANIMATIONS (Tab 1) --- */

        /* FIXED: Ripple Expand Loop to sync with 6s whale cycle */
        @keyframes ripple-loop-1 {
            0%, 28% { r: 0; opacity: 0; stroke-width: 0; }
            30% { r: 0; opacity: 0.8; stroke-width: 3; }
            45% { r: 40; opacity: 0; stroke-width: 0; }
            100% { r: 40; opacity: 0; stroke-width: 0; }
        }
        .animate-ripple-1 {
            animation: ripple-loop-1 6s linear infinite;
        }

        @keyframes ripple-loop-2 {
            0%, 68% { r: 0; opacity: 0; stroke-width: 0; }
            70% { r: 0; opacity: 0.8; stroke-width: 3; }
            85% { r: 40; opacity: 0; stroke-width: 0; }
            100% { r: 40; opacity: 0; stroke-width: 0; }
        }
        .animate-ripple-2 {
            animation: ripple-loop-2 6s linear infinite;
        }

        /* Specific Reveals for Whale Tab */
        @keyframes reveal-whale-1 {
            0%, 25% { opacity: 0; transform: translateY(5px); }
            30%, 85% { opacity: 1; transform: translateY(0); }
            95%, 100% { opacity: 0; transform: translateY(-5px); }
        }
        .animate-reveal-1 {
            animation: reveal-whale-1 6s ease-out infinite; /* Matches 6s loop */
        }

        @keyframes reveal-whale-2 {
            0%, 65% { opacity: 0; transform: translateY(5px); }
            70%, 85% { opacity: 1; transform: translateY(0); }
            95%, 100% { opacity: 0; transform: translateY(-5px); }
        }
        .animate-reveal-2 {
            animation: reveal-whale-2 6s ease-out infinite; /* Matches 6s loop */
        }
        
        /* --- EMERGING TRENDS ANIMATIONS (Tab 3 - Purple) --- */
        @keyframes pop-fade-scale {
            0% { opacity: 0; transform: scale(0); }
            20% { opacity: 1; transform: scale(1.1); }
            25% { transform: scale(1); }
            80% { opacity: 1; transform: scale(1); }
            100% { opacity: 0; transform: scale(0.9); }
        }
        .animate-pop-1 { animation: pop-fade-scale 4s ease-in-out infinite 0s; }
        .animate-pop-2 { animation: pop-fade-scale 4s ease-in-out infinite 1s; }
        .animate-pop-3 { animation: pop-fade-scale 5s ease-in-out infinite 2s; }
        .animate-pop-4 { animation: pop-fade-scale 4.5s ease-in-out infinite 0.5s; }


        /* 4. Slow Drift for Blur */
        @keyframes drift-slow {
            0% { transform: translate(-50%, -50%) translate(0, 0); }
            33% { transform: translate(-50%, -50%) translate(20px, -20px); }
            66% { transform: translate(-50%, -50%) translate(-20px, 20px); }
            100% { transform: translate(-50%, -50%) translate(0, 0); }
        }
        .animate-drift-slow {
            animation: drift-slow 10s ease-in-out infinite;
        }

        /* --- COMMUNITY SECTION ANIMATIONS --- */
        @keyframes grid-scroll {
            0% { transform: rotateX(60deg) translateY(0); }
            100% { transform: rotateX(60deg) translateY(-50px); }
        }
        @keyframes float-particle-fast {
            0% { transform: translateY(0) translateX(0) rotate(0deg); opacity: 0; }
            20% { opacity: 0.6; }
            80% { opacity: 0.6; }
            100% { transform: translateY(-200px) translateX(${Math.random() > 0.5 ? 50 : -50}px) rotate(360deg); opacity: 0; }
        }
        @keyframes pulse-fast {
            0%, 100% { opacity: 0.5; transform: scale(1); }
            50% { opacity: 0.8; transform: scale(1.05); }
        }
        .animate-pulse-fast {
            animation: pulse-fast 3s infinite ease-in-out;
        }
        .animate-float-particle-fast {
            animation: float-particle-fast infinite linear;
        }

        /* --- MODAL ANIMATIONS --- */
        @keyframes modal-in {
            0% { opacity: 0; transform: scale(0.95) translateY(10px); }
            100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-modal-in {
            animation: modal-in 0.3s ease-out forwards;
        }
        @keyframes fade-in {
            0% { opacity: 0; }
            100% { opacity: 1; }
        }
        .animate-fade-in {
            animation: fade-in 0.2s ease-out forwards;
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }
        ::-webkit-scrollbar-track {
          background: #090c1d;
        }
        ::-webkit-scrollbar-thumb {
          background: #1f2937;
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #374151;
        }
      `}</style>

      <Navbar />

      {/* Hero Section */}
      <main className="pt-20 lg:pt-28"> {/* Reduced top padding on mobile */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile: Radar first (flex-col-reverse), then text. Desktop: Grid layout (lg:grid) */}
          <div className="flex flex-col-reverse lg:grid lg:grid-cols-2 gap-8 lg:gap-8 items-center py-4 lg:py-12"> {/* Reduced vertical padding on mobile */}
            
            {/* Right Content - Radar Visualization (Top on Mobile) */}
            <div className="relative w-full order-1 lg:order-2 max-w-[450px] mx-auto lg:max-w-full">
                {/* Max height applied here for mobile reduction */}
                <div className="relative w-full aspect-square max-w-[550px] mx-auto perspective-1000 group max-h-[300px] sm:max-h-full">
                    <Radar />
                </div>
            </div>

            {/* Left Content (Second on Mobile) */}
            <div className="space-y-4 lg:space-y-8 z-20 order-2 lg:order-1">
              
              {/* Reduced text size */}
              <h1 className="text-4xl lg:text-6xl font-bold tracking-tight leading-[1.1]">
                Where onchain data becomes <br/>
                <TypewriterText />
              </h1>

              <p className="text-base text-gray-400 max-w-xl leading-relaxed lg:text-lg">
                <strong className="text-white">AlphaRadarX</strong> reveals Smart Money movements, whale flows, and cross-chain signals in real time, giving traders true X-Ray vision into the crypto markets.
              </p>

              <div className="flex flex-col sm:flex-row items-start gap-4 pt-2 lg:pt-4">
                <a 
                  href="https://alpharadarx.gitbook.io/alpharadarx-whitepaper"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-r from-[#36A4E6] to-[#757EE0] text-white px-8 py-4 rounded-full font-bold hover:scale-105 transition-transform shadow-[0_0_40px_rgba(54,164,230,0.5)] hover:shadow-[0_0_60px_rgba(54,164,230,0.7)] flex items-center gap-2 w-full sm:w-auto justify-center group"
                >
                  Whitepaper <ArrowRight className="group-hover:translate-x-1 transition-transform" />
                </a>
                <a 
                  href="https://t.me/AlphaRadar_X" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-transparent border border-white/20 text-white px-8 py-4 rounded-full font-bold hover:bg-white/5 transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
                >
                  <MessageCircle size={20} /> Join Community
                </a>
              </div>

            </div>
          </div>
        </div>

        {/* Marquee Section */}
        <div className="mt-12">
            <LogoMarquee />
        </div>
        
        {/* Stats Section (Correctly Stacked) */}
        <StatsSection />

        {/* Features Preview (UPDATED: Interactive Tabs) */}
        <FeatureTabs />

        {/* Platform Suite Section */}
        <PlatformSuite />

        {/* NEW: Utility Section (Added here) */}
        <UtilitySection />

        {/* NEW: FAQ Section (Added here) */}
        <FAQSection />

        {/* NEW: Community Section (Added here) */}
        <CommunitySection />

      </main>

      {/* NEW: Cookie Banner & Modal */}
      {showCookies && !showPreferences && (
        <CookieBanner 
          onAccept={handleAcceptAll} 
          onPreferences={() => setShowPreferences(true)} 
        />
      )}
      
      {showPreferences && (
        <CookiePreferencesModal 
          onClose={() => setShowPreferences(false)} 
          onSave={handleSavePreferences} 
        />
      )}

      {/* --- ENHANCED FOOTER --- */}
      <footer className="relative bg-[#04060e] border-t border-white/10 pt-20 pb-10 overflow-hidden mt-12">
          {/* Background Glow */}
          <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-[#36A4E6]/5 blur-[150px] rounded-full pointer-events-none translate-y-1/3 translate-x-1/3"></div>
          <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-[#757EE0]/5 blur-[120px] rounded-full pointer-events-none -translate-y-1/2 -translate-x-1/2"></div>

          <div className="max-w-7xl mx-auto px-4 relative z-10">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-8 mb-16">
                  
                  {/* Brand Column */}
                  <div className="md:col-span-12 lg:col-span-4">
                      <img src={ASSETS.logo} alt="AlphaRadarX" className="h-10 mb-6" />
                      <p className="text-gray-400 text-sm leading-relaxed max-w-md mb-8">
                          Where onchain data becomes intelligence. Reveal Smart Money movements, whale flows, and early trends in real time before they hit the charts.
                      </p>
                      
                      {/* Social Icons Row */}
                      <div className="flex items-center gap-4">
                          <a href="https://x.com/alpharaderx" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-[#36A4E6]/20 hover:border-[#36A4E6]/50 hover:shadow-[0_0_15px_rgba(54,164,230,0.3)] transition-all duration-300">
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                              </svg>
                          </a>
                          <a href="https://t.me/AlphaRadar_X" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-[#36A4E6]/20 hover:border-[#36A4E6]/50 hover:shadow-[0_0_15px_rgba(54,164,230,0.3)] transition-all duration-300">
                              <svg className="w-5 h-5 -ml-1 mt-0.5" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M20.665 3.717l-17.73 6.837c-1.21.486-1.203 1.161-.222 1.462l4.552 1.42 10.532-6.645c.498-.303.953-.14.579.192l-8.533 7.701h-.002l.002.001-.314 4.692c.46 0 .663-.211.921-.46l2.211-2.15 4.599 3.397c.848.467 1.457.227 1.668-.785l3.019-14.228c.309-1.239-.473-1.8-1.282-1.434z" />
                              </svg>
                          </a>
                      </div>
                  </div>

                  {/* Spacer for desktop */}
                  <div className="hidden lg:block lg:col-span-3"></div>

                  {/* Platform Links */}
                  <div className="md:col-span-6 lg:col-span-2">
                      <h4 className="text-white font-bold mb-6 uppercase tracking-wider text-sm">Platform</h4>
                      <ul className="space-y-4">
                          <li>
                              <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="text-gray-400 hover:text-[#36A4E6] text-sm transition-colors">
                                  App Dashboard
                              </button>
                          </li>
                          <li>
                              <a href="#features" className="text-gray-400 hover:text-[#36A4E6] text-sm transition-colors">
                                  Core Features
                              </a>
                          </li>
                          <li>
                              <a href="#suite" className="text-gray-400 hover:text-[#36A4E6] text-sm transition-colors">
                                  Intelligence Suite
                              </a>
                          </li>
                          <li>
                              <a href="#token" className="text-gray-400 hover:text-[#36A4E6] text-sm transition-colors">
                                  $ARX Utility
                              </a>
                          </li>
                      </ul>
                  </div>

                  {/* Community & Docs Links */}
                  <div className="md:col-span-6 lg:col-span-3">
                      <h4 className="text-white font-bold mb-6 uppercase tracking-wider text-sm">Resources</h4>
                      <ul className="space-y-4">
                          <li>
                              <a href="https://alpharadarx.gitbook.io/alpharadarx-whitepaper" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-gray-400 hover:text-[#36A4E6] text-sm transition-colors group">
                                  Official Whitepaper <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                              </a>
                          </li>
                          <li>
                              <a href="https://x.com/alpharaderx" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#36A4E6] text-sm transition-colors">
                                  X (Twitter)
                              </a>
                          </li>
                          <li>
                              <a href="https://t.me/AlphaRadar_X" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#36A4E6] text-sm transition-colors">
                                  Telegram Community
                              </a>
                          </li>
                          <li>
                              <a href="https://t.me/AlphaRadar_Xnews" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#36A4E6] text-sm transition-colors">
                                  AlphaRadarX News
                              </a>
                          </li>
                      </ul>
                  </div>

              </div>

              {/* Bottom Copyright Bar */}
              <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
                  <div className="text-gray-500 text-sm">
                      © {new Date().getFullYear()} AlphaRadarX. All rights reserved.
                  </div>
                  <div className="flex items-center gap-6">
                      <button 
                        onClick={() => setShowCookies(true)} 
                        className="text-gray-500 hover:text-[#36A4E6] text-sm transition-colors cursor-pointer focus:outline-none"
                      >
                          Cookies
                      </button>
                  </div>
              </div>
          </div>
      </footer>
    </div>
  );
};

export default App;
